import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Loader2, X, Crown } from "lucide-react";
import { apiClient, useAuth } from "@/App";

export default function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("polling"); // polling, success, error
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (!sessionId || hasProcessed.current) return;
    hasProcessed.current = true;

    let attempts = 0;
    const maxAttempts = 8;
    const interval = 2000;

    const poll = async () => {
      try {
        const res = await apiClient.get(`/subscription/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
          await checkAuth(); // Refresh user data with new subscription
          return;
        }
        if (res.data.status === "expired") {
          setStatus("error");
          return;
        }
      } catch {
        // Continue polling
      }

      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, interval);
      } else {
        setStatus("error");
      }
    };

    poll();
  }, [sessionId, checkAuth]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="bg-card border-border max-w-md w-full">
        <CardContent className="p-8 text-center space-y-4">
          {status === "polling" && (
            <>
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <Loader2 className="h-7 w-7 text-primary animate-spin" />
              </div>
              <h2 className="font-heading text-xl font-bold">Processing payment...</h2>
              <p className="text-muted-foreground text-sm">Please wait while we confirm your payment.</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto" data-testid="payment-success-icon">
                <Crown className="h-7 w-7 text-primary" />
              </div>
              <h2 className="font-heading text-xl font-bold" data-testid="payment-success-title">Welcome to Pro!</h2>
              <p className="text-muted-foreground text-sm">
                Your upgrade is complete. All Pro features are now unlocked.
              </p>
              <Button
                onClick={() => navigate("/dashboard")}
                className="w-full bg-primary text-primary-foreground"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
                <X className="h-7 w-7 text-red-500" />
              </div>
              <h2 className="font-heading text-xl font-bold">Payment issue</h2>
              <p className="text-muted-foreground text-sm">
                We couldn't confirm your payment. Please check your email for confirmation or try again.
              </p>
              <Button
                onClick={() => navigate("/settings")}
                variant="outline"
                className="w-full"
              >
                Back to Settings
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
